// src/pages/talleres/editar/TalleresEditar.jsx
import React, { useState, useEffect } from "react";
import { Steps, Button, Space, Typography, Divider, Card, App, Upload, Image, Alert, Form } from "antd";
import {
  ArrowRightOutlined, ArrowLeftOutlined, DeleteOutlined,
  CameraOutlined, SaveOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import "dayjs/locale/es";

// Components
import InfoBasicaForm from "./InfoBasicaForm";
import FechasForm from "./FechasForm";
import ModalidadesForm from "./ModalidadesForm";
import ResumenForm from "./ResumenForm";

// Hooks & Utils
import useTallerEditar from "./useTallerEditar";
import { CAMPOS_VALORES_EXT, parseCLP } from "./utils/tallerUtils";
import { can, puedeAprobar, requiereAprobacion } from "../../../../../config/rbac";
import { STEPS } from "./constants";

// Services
import Talleres from "../../../../../service/Talleres";

// Providers
import { useNotify, useWithNotify } from "../../../../../providers/NotificationProvider";
import useAuthContext from "../../../../../hooks/useAuthContext";

// Router
import { useNavigate } from "react-router-dom";

// Icons
import { History } from "lucide-react";

dayjs.locale("es");
dayjs.extend(utc);

const { Title } = Typography;

export default function TalleresEditar() {
  const id = window.location.pathname.split("/").filter(Boolean).pop();

  const {
    loading, saving, setSaving,
    data, setData,
    recintos, recintosLoading,
    formInfo, formFechas, formPlanes,
    planActive, setPlanActive,
    usuariosAutorizados,
    setUsuariosAutorizados,
  } = useTallerEditar(id);

  const notify = useNotify();
  const withNotify = useWithNotify();
  const navigate = useNavigate();
  const { modal } = App.useApp();
  const { user } = useAuthContext();

  // Estados
  const [togglingEstado, setTogglingEstado] = useState(false);
  const [togglingInsc, setTogglingInsc] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [esGratuito, setEsGratuito] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [deletingImage, setDeletingImage] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Permisos. `isJefeRecinto` pasó a llamarse por lo que de verdad significa acá:
  // que los cambios de esta persona van a la cola de revisión en vez de aplicarse.
  // Eso ya no depende del nombre del rol sino de la bandera del permiso.
  const canEdit = can(user, "talleres", "editar");
  const pasaPorRevision = requiereAprobacion(user, "talleres");
  // Activar, pausar y eliminar son acciones directas: las hace quien no necesita
  // que le aprueben.
  const canManageActions = puedeAprobar(user, "talleres");
  const isJefeRecinto = pasaPorRevision;

  useEffect(() => {
    if (data) setEsGratuito(data.esGratuito || false);
  }, [data]);

  useEffect(() => {
    if (data?.imagen?.url) setPreviewImage(data.imagen.url);
  }, [data?.imagen]);

  useEffect(() => {
    if (currentStep > 0) {
      setHasUnsavedChanges(true);
    }
  }, [currentStep]);

  const handleNext = async () => {
    try {
      if (currentStep === 0) {
        const info = await formInfo.validateFields();

        if (info.edadMax && info.edadMin && info.edadMax < info.edadMin) {
          notify.error("La edad máxima debe ser mayor o igual a la mínima");
          return;
        }

        // Validar usuarios autorizados si es academia
        if (info.tipoTaller === 'academia' && (!usuariosAutorizados || usuariosAutorizados.length === 0)) {
          notify.error("Las academias deben tener al menos un usuario autorizado");
          return;
        }
      } else if (currentStep === 1) {
        const fechas = await formFechas.validateFields();
        const { inscripcionInicio, inscripcionFin, fechasActividades } = fechas;

        // Validar fechas de inscripción con hora
        if (inscripcionInicio && inscripcionFin) {
          if (dayjs(inscripcionInicio).isAfter(dayjs(inscripcionFin))) {
            notify.error("La apertura de inscripciones debe ser antes del cierre");
            return;
          }
        }

        // Validar fechas de actividades
        if (fechasActividades?.[0] && fechasActividades?.[1]) {
          if (dayjs(fechasActividades[0]).isAfter(dayjs(fechasActividades[1]))) {
            notify.error("La fecha de inicio de actividades debe ser anterior a la fecha de fin");
            return;
          }
        }

        // Validar coherencia entre inscripción y actividades
        if (inscripcionFin && fechasActividades?.[0]) {
          if (dayjs(inscripcionFin).isAfter(dayjs(fechasActividades[0]))) {
            notify.error("Las inscripciones deben cerrar antes o el mismo día del inicio de actividades");
            return;
          }
        }
      } else if (currentStep === 2) {
        if (!esGratuito) {
          const activos = Object.keys(planActive).filter(p => planActive[p]);
          if (activos.length === 0) {
            notify.error("Debe activar al menos una modalidad de pago");
            return;
          }
          await formPlanes.validateFields();
        }
      }

      setCurrentStep(currentStep + 1);
      setHasUnsavedChanges(true);
    } catch (error) {
      console.error('Error validación:', error);
      notify.error("Por favor completa todos los campos requeridos");
    }
  };

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleGoBack = () => {
    navigate(`/admin/talleres/lista`);
  };

  const guardarCambios = async () => {
    try {
      setSaving(true);

      const info = await formInfo.validateFields();
      const fechas = await formFechas.validateFields();

      const { inscripcionInicio, inscripcionFin, fechasActividades } = fechas;
      const actIni = fechasActividades?.[0];
      const actFin = fechasActividades?.[1];

      // Validaciones
      if (inscripcionInicio && inscripcionFin && dayjs(inscripcionInicio).isAfter(dayjs(inscripcionFin))) {
        throw new Error("La apertura debe ser antes del cierre");
      }

      if (actIni && actFin && dayjs(actIni).isAfter(dayjs(actFin))) {
        throw new Error("Rango de actividades inválido");
      }

      if (inscripcionFin && actIni && dayjs(inscripcionFin).isAfter(dayjs(actIni))) {
        throw new Error("Las inscripciones deben cerrar antes o el mismo día del inicio de actividades");
      }

      // Validar usuarios autorizados si es academia
      if (info.tipoTaller === 'academia' && (!usuariosAutorizados || usuariosAutorizados.length === 0)) {
        throw new Error("Las academias deben tener al menos un usuario autorizado");
      }

      let modalidadPago = [];

      if (!esGratuito) {
        const activos = Object.keys(planActive).filter(p => planActive[p]);
        if (activos.length === 0) {
          throw new Error("Debe activar al menos una modalidad de pago o marcar el taller como gratuito");
        }

        const valores = formPlanes.getFieldValue("valores") || {};
        const algunoBase = activos.some(plan => {
          const vp = valores?.[plan]?.valorParticular;
          const num = typeof vp === "number" ? vp : parseCLP(vp);
          return num != null && !Number.isNaN(num) && num > 0;
        });

        if (!algunoBase) {
          throw new Error("Debe ingresar 'Valor particular' en al menos un plan activo");
        }

        await formPlanes.validateFields();

        const modalidadesExistentes = data.modalidadPago || [];
        const modalidadesActualizadas = activos.map(plan => {
          const vals = valores[plan] || {};
          const existente = modalidadesExistentes.find(mp => mp.plan === plan);

          if (existente) {
            let cambio = false;
            CAMPOS_VALORES_EXT.forEach(c => {
              const v = vals[c.key];
              const numValue = v !== undefined && v !== null && v !== ""
                ? (typeof v === "number" ? v : parseCLP(v))
                : null;
              if (numValue !== null && existente[c.key] !== numValue) cambio = true;
            });
            if (!cambio) return existente;
          }

          const obj = existente ? { ...existente } : { plan };
          CAMPOS_VALORES_EXT.forEach(c => {
            const v = vals[c.key];
            if (v !== undefined && v !== null && v !== "") {
              obj[c.key] = typeof v === "number" ? v : parseCLP(v);
            }
          });
          return obj;
        });

        modalidadPago = modalidadesActualizadas;
      }

      // 🆕 Construir payload con fechas correctas e intensidad
      const payload = {
        nombre: info.nombre,
        tipoActividad: info.tipoActividad,
        recinto: info.recinto,
        descripcion: info.descripcion,
        tipoDuracionSuscripcion: info.tipoDuracionSuscripcion,
        nivelIntensidad: info.nivelIntensidad || 'moderada', // 🆕 NUEVO
        edadMin: info.edadMin,
        edadMax: info.edadMax ?? null,
        inscripcionInicio: inscripcionInicio ? dayjs(inscripcionInicio).toISOString() : null,
        inscripcionFin: inscripcionFin ? dayjs(inscripcionFin).toISOString() : null,
        actividadesInicio: actIni ? dayjs(actIni).startOf('day').toISOString() : null,
        actividadesFin: actFin ? dayjs(actFin).endOf('day').toISOString() : null,
        liberarCuposPorBaja: info.liberarCuposPorBaja ?? true,
        soloTarjetaVecino: info.soloTarjetaVecino || false,
        requiereMatricula: info.requiereMatricula || false,
        tieneIVA: info.tieneIVA || false,
        accesoPiscina: info.accesoPiscina || false,
        avisoVencimiento: info.avisoVencimiento || false,
        tipoTaller: info.tipoTaller || 'libre',
        usuariosAutorizados: usuariosAutorizados || [],
        esGratuito: esGratuito,
        modalidadPago: modalidadPago,
        horarios: (data.horarios || []).map(h => h._id).filter(Boolean)
      };

      console.log('📤 Payload a guardar:', payload);
      if (payload.inscripcionInicio) {
        console.log('🕐 inscripcionInicio (local):', dayjs(inscripcionInicio).format('DD/MM/YYYY HH:mm'));
        console.log('🕐 inscripcionInicio (ISO):', payload.inscripcionInicio);
      }
      if (payload.inscripcionFin) {
        console.log('🕐 inscripcionFin (local):', dayjs(inscripcionFin).format('DD/MM/YYYY HH:mm'));
        console.log('🕐 inscripcionFin (ISO):', payload.inscripcionFin);
      }

      if (!payload.nombre || !payload.tipoActividad || !payload.recinto) {
        throw new Error("Faltan datos básicos requeridos");
      }
      if (!esGratuito && (!modalidadPago || modalidadPago.length === 0)) {
        throw new Error("El taller no es gratuito pero no tiene modalidades de pago");
      }

      if (isJefeRecinto) {
        modal.info({
          title: "Cambio enviado para aprobación",
          content: (
            <div className="space-y-2">
              <p>Tu solicitud de cambio ha sido enviada correctamente.</p>
              <p>Los cambios se aplicarán una vez que un <strong>Administrador</strong> o <strong>Coordinador</strong> los apruebe.</p>
            </div>
          ),
          okText: "Entendido",
        });
      }

      await withNotify(
        Talleres.Update(id, payload),
        {
          loading: "Guardando cambios...",
          success: isJefeRecinto ? "Cambio enviado para aprobación" : "Taller actualizado",
          error: "Error al guardar"
        }
      );

      setData(prev => ({ ...prev, ...payload }));
      setHasUnsavedChanges(false);
      navigate(`/admin/talleres/lista`);

    } catch (e) {
      console.error('❌ Error guardando:', e);
      
      if (e.code === 'MATRICULA_NOT_CONFIGURED') {
        notify.error('El recinto seleccionado no tiene configuración de matrícula. Configure la matrícula primero.');
      } else {
        notify.error(e.message || "No se pudo guardar");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleUploadImage = async (file) => {
    try {
      setUploadingImage(true);

      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        notify.error('Solo se permiten archivos de imagen');
        return false;
      }

      // El navegador la recomprime a WebP antes de subirla (libs/optimizarImagen),
      // así que el original puede venir pesado: una foto de celular queda en ~100 KB.
      const isLt20M = file.size / 1024 / 1024 < 20;
      if (!isLt20M) {
        notify.error('La imagen debe ser menor a 20MB');
        return false;
      }

      await withNotify(
        Talleres.UploadImage(id, file),
        {
          loading: 'Subiendo imagen...',
          success: 'Imagen subida exitosamente',
          error: 'Error al subir la imagen'
        }
      );

      const tallerActualizado = await Talleres.getById(id);
      setData(tallerActualizado);
      setPreviewImage(tallerActualizado?.imagen?.url);

      return false;
    } catch (error) {
      console.error('Error al subir imagen:', error);
      notify.error(error.message || 'Error al subir la imagen');
      return false;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteImage = () => {
    modal.confirm({
      title: '¿Eliminar imagen?',
      content: 'Esta acción no se puede deshacer.',
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          setDeletingImage(true);

          await withNotify(
            Talleres.DeleteImage(id),
            {
              loading: 'Eliminando imagen...',
              success: 'Imagen eliminada exitosamente',
              error: 'Error al eliminar la imagen'
            }
          );

          const tallerActualizado = await Talleres.getById(id);
          setData(tallerActualizado);
          setPreviewImage(null);
        } catch (error) {
          console.error('Error al eliminar imagen:', error);
          notify.error(error.message || 'Error al eliminar la imagen');
        } finally {
          setDeletingImage(false);
        }
      }
    });
  };

  const estadoActual = data?.estado === "activo" ? "activo" : "pausado";
  const inscActual = data?.inscripcionesEstado === "abiertas" ? "abiertas" : "pausadas";

  const onToggleEstado = async () => {
    const estabaActivo = estadoActual === "activo";

    if (!estabaActivo) {
      if ((data?.horarios?.length || 0) === 0) {
        notify.warning("Para activar el taller debe existir al menos un horario");
        return;
      }
      if (!data?.enCicloActivo) {
        notify.warning("Para activar el taller debe existir un ciclo activo");
        return;
      }
    }

    const nextEstado = estabaActivo ? "pausado" : "activo";
    setTogglingEstado(true);

    try {
      await withNotify(
        Talleres.ToggleActivo(id, { estado: nextEstado }),
        {
          loading: nextEstado === "activo" ? "Activando taller..." : "Pausando taller...",
          success: nextEstado === "activo" ? "Taller activado" : "Taller pausado",
          error: "Error al cambiar estado"
        }
      );
      setData(prev => ({ ...prev, estado: nextEstado }));
    } finally {
      setTogglingEstado(false);
    }
  };

  const onToggleInscripciones = async () => {
    if (estadoActual !== "activo") {
      notify.warning("El taller debe estar activo para cambiar inscripciones");
      return;
    }
    if (!data?.enCicloActivo) {
      notify.warning("Debe existir un ciclo activo para abrir inscripciones");
      return;
    }

    const estabanAbiertas = inscActual === "abiertas";
    const nextInsc = estabanAbiertas ? "pausadas" : "abiertas";
    setTogglingInsc(true);

    try {
      await withNotify(
        Talleres.ToggleInscripciones(id, { inscripcionesAbiertas: nextInsc }),
        {
          loading: nextInsc === "abiertas" ? "Abriendo inscripciones..." : "Pausando inscripciones...",
          success: nextInsc === "abiertas" ? "Inscripciones abiertas" : "Inscripciones pausadas",
          error: "Error al cambiar inscripciones"
        }
      );
      setData(prev => ({ ...prev, inscripcionesEstado: nextInsc }));
    } finally {
      setTogglingInsc(false);
    }
  };

  const onDelete = async () => {
    modal.confirm({
      title: "¿Eliminar taller?",
      content: "Esta acción no se puede deshacer.",
      okText: "Eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        await withNotify(
          Talleres.Delete(id),
          { loading: "Eliminando...", success: "Taller eliminado", error: "Error" }
        );
        navigate("/admin/talleres/lista");
      }
    });
  };

  const renderFooterButtons = () => {
    return (
      <Space size="middle" className="w-full justify-between">
        <Button
          size="large"
          onClick={handleGoBack}
          icon={<ArrowLeftOutlined />}
        >
          {currentStep === 0 ? 'Volver a lista' : 'Cancelar'}
        </Button>

        <Space>
          {currentStep > 0 && (
            <Button
              size="large"
              onClick={handlePrev}
            >
              Anterior
            </Button>
          )}

          {currentStep < 3 && (
            <Button
              type="primary"
              size="large"
              onClick={handleNext}
              icon={<ArrowRightOutlined />}
            >
              Siguiente
            </Button>
          )}

          {currentStep === 3 && canEdit && (
            <Button
              type="primary"
              size="large"
              onClick={guardarCambios}
              loading={saving}
              icon={<SaveOutlined />}
            >
              {isJefeRecinto ? "Enviar para aprobación" : "Guardar cambios"}
            </Button>
          )}
        </Space>
      </Space>
    );
  };

  if (loading || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-lg mb-2">Cargando taller...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4">
        <div className="mb-6 flex items-center justify-between">
          <Title level={3} className="!mb-0">Editar Taller</Title>

          {canManageActions && (
            <Space>
              <Button
                icon={<History />}
                onClick={() => navigate(`/admin/talleres/editar/${data._id}/historial-cambios`)}
              >
                Historial
              </Button>
              <Button
                type={estadoActual === "activo" ? "default" : "primary"}
                loading={togglingEstado}
                onClick={onToggleEstado}
              >
                {estadoActual === "activo" ? "Pausar taller" : "Activar taller"}
              </Button>
              <Button danger onClick={onDelete}>Eliminar</Button>
            </Space>
          )}
        </div>

        <Card>
          {isJefeRecinto && (
            <Alert
              message="Modo con aprobación"
              description="Los cambios requieren aprobación de Administrador o Coordinador."
              type="info"
              showIcon
              className="mb-6"
            />
          )}

          <Steps
            current={currentStep}
            items={STEPS}
            className="mb-8"
          />

          <div style={{ display: currentStep === 0 ? 'block' : 'none' }}>
            <Card title="📷 Imagen del Taller" loading={uploadingImage || deletingImage} className="mb-6">
              <div className="flex flex-col gap-4">
                {previewImage ? (
                  <div className="flex flex-col gap-3">
                    <div className="flex justify-center">
                      <Image
                        src={previewImage}
                        alt="Imagen del taller"
                        width={300}
                        style={{ maxHeight: '300px', objectFit: 'cover', borderRadius: '8px' }}
                      />
                    </div>
                    <div className="flex gap-2 justify-center">
                      <Upload
                        beforeUpload={handleUploadImage}
                        maxCount={1}
                        accept="image/*"
                        showUploadList={false}
                      >
                        <Button icon={<CameraOutlined />} loading={uploadingImage}>
                          Cambiar imagen
                        </Button>
                      </Upload>
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        loading={deletingImage}
                        onClick={handleDeleteImage}
                      >
                        Eliminar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Upload
                    beforeUpload={handleUploadImage}
                    maxCount={1}
                    accept="image/*"
                    listType="picture-card"
                    showUploadList={false}
                  >
                    <div className="text-center">
                      <CameraOutlined style={{ fontSize: '32px', color: '#bfbfbf' }} />
                      <div className="mt-2">Subir imagen</div>
                    </div>
                  </Upload>
                )}
              </div>
            </Card>

            <Form form={formInfo} layout="vertical" disabled={!canEdit}>
              <InfoBasicaForm
                form={formInfo}
                recintos={recintos}
                recintosLoading={recintosLoading}
                disabled={!canEdit}
                usuariosAutorizados={usuariosAutorizados}
                setUsuariosAutorizados={setUsuariosAutorizados}
              />
            </Form>
          </div>

          <div style={{ display: currentStep === 1 ? 'block' : 'none' }}>
            <Form form={formFechas} layout="vertical" disabled={!canEdit}>
              <FechasForm
                form={formFechas}
                disabled={!canEdit}
              />
            </Form>
          </div>

          <div style={{ display: currentStep === 2 ? 'block' : 'none' }}>
            <Form form={formPlanes} layout="vertical" disabled={!canEdit}>
              <ModalidadesForm
                form={formPlanes}
                planActive={planActive}
                setPlanActive={setPlanActive}
                esGratuito={esGratuito}
                setEsGratuito={setEsGratuito}
                disabled={!canEdit}
                fechasActividades={formFechas.getFieldValue('fechasActividades')} 
              />
            </Form>
          </div>

          <div style={{ display: currentStep === 3 ? 'block' : 'none' }}>
            <ResumenForm
              data={data}
              formInfo={formInfo}
              formFechas={formFechas}
              formPlanes={formPlanes}
              planActive={planActive}
              esGratuito={esGratuito}
              recintos={recintos}
            />
          </div>

          <Divider />
          {renderFooterButtons()}
        </Card>
      </div>
    </div>
  );
}
